/*
  Saunterly interactions. One small ES module of custom elements, no libraries.
  Horizon's cart drawer is reused: adds dispatch its CartLinesUpdateEvent so the
  drawer re-renders, opens and keeps its own count in sync.
*/

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const formatNumber = (n) => Math.round(n).toLocaleString('en-US');
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

let eventsModule;
async function loadEvents() {
  if (eventsModule !== undefined) return eventsModule;
  try {
    eventsModule = await import('@shopify/events');
  } catch (error) {
    eventsModule = null;
  }
  return eventsModule;
}

function define(name, constructor) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}

/* ---------- Toast (fallback feedback when there is no cart drawer) ---------- */
let toastTimer;
function toast(message) {
  let el = document.querySelector('.sl-toast');
  if (!el) {
    el = document.createElement('div');
    el.className = 'sl-toast';
    el.setAttribute('role', 'status');
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2800);
}

/* ---------- Cart helpers ---------- */
function cartDrawer() {
  return document.querySelector('theme-drawer#cart-drawer');
}

function openCartDrawer() {
  const drawer = cartDrawer();
  if (drawer && typeof drawer.open === 'function') {
    if (!drawer.isOpen) drawer.open();
    return true;
  }
  return false;
}

async function fetchCart() {
  const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

function setCartCount(count) {
  document.querySelectorAll('[data-sl-cart-count]').forEach((el) => {
    const changed = el.textContent !== String(count);
    el.textContent = String(count);
    if (changed && !reducedMotion()) {
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    }
  });
  document.querySelectorAll('[data-sl-cart-label]').forEach((el) => {
    el.textContent = el.dataset.template ? el.dataset.template.replace('[count]', count) : String(count);
  });
}

/**
 * Adds one line to the cart through the AJAX API and tells Horizon about it.
 * @param {Element} source - element the cart event is dispatched from
 * @param {string|number} variantId
 * @param {number} quantity
 */
async function addToCart(source, variantId, quantity = 1) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const events = await loadEvents();
  let deferred = null;

  if (events && events.CartLinesUpdateEvent) {
    deferred = events.CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );
  }

  const settle = (cart, extra) => {
    if (!deferred) return;
    deferred.resolve({
      cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'saunterly-buy-box', itemCount: quantity, ...extra },
    });
  };

  let data;
  try {
    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], sections: sectionIds.join(',') }),
    });
    data = await response.json();
    if (!response.ok || data.status) {
      const cart = await fetchCart();
      settle(cart, { didError: true });
      setCartCount(cart.item_count);
      throw new Error(data.description || data.message || 'add-failed');
    }
  } catch (error) {
    if (deferred && !data) deferred.reject(error);
    throw error;
  }

  const cart = await fetchCart();
  settle(cart, { sections: data.sections, didError: false });
  setCartCount(cart.item_count);
  return cart;
}

/* ---------- Header: menu toggle + cart button ---------- */
class SaunterlyHeader extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sl-menu-toggle]');
    this.drawer = this.querySelector('[data-sl-menu]');
    this.cart = this.querySelector('[data-sl-cart-open]');

    this.button?.addEventListener('click', this.toggle);
    this.drawer?.addEventListener('click', this.onDrawerClick);
    this.cart?.addEventListener('click', this.onCart);
    document.addEventListener('keydown', this.onKey);
    this.listenForCart();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKey);
    if (this.cartListener) document.removeEventListener(this.cartEventName, this.cartListener);
  }

  toggle = () => {
    const open = this.button.getAttribute('aria-expanded') !== 'true';
    this.button.setAttribute('aria-expanded', String(open));
    this.drawer.hidden = !open;
    if (open) this.drawer.querySelector('a')?.focus();
  };

  close() {
    if (!this.button || this.button.getAttribute('aria-expanded') !== 'true') return;
    this.button.setAttribute('aria-expanded', 'false');
    this.drawer.hidden = true;
  }

  onDrawerClick = (event) => {
    if (event.target.closest('a')) this.close();
  };

  onKey = (event) => {
    if (event.key === 'Escape' && this.button?.getAttribute('aria-expanded') === 'true') {
      this.close();
      this.button.focus();
    }
  };

  onCart = (event) => {
    if (openCartDrawer()) event.preventDefault();
  };

  async listenForCart() {
    const events = await loadEvents();
    if (!events || !events.StandardEvents) return;
    this.cartEventName = events.StandardEvents.cartLinesUpdate;
    this.cartListener = (event) => {
      event.promise
        ?.then(({ cart, detail }) => {
          const count = cart?.totalQuantity ?? detail?.items?.reduce((sum, item) => sum + item.quantity, 0);
          if (typeof count === 'number') setCartCount(count);
        })
        .catch(() => {});
    };
    document.addEventListener(this.cartEventName, this.cartListener);
  }
}
define('sl-header', SaunterlyHeader);

/* ---------- Walking-day planner ---------- */
class SaunterlyDayPlanner extends HTMLElement {
  connectedCallback() {
    this.rate = Number(this.dataset.rate) || 100;
    this.numberEl = this.querySelector('[data-sl-steps]');
    this.minutesEl = this.querySelector('[data-sl-minutes]');
    this.liveEl = this.querySelector('[data-sl-live]');
    this.buttons = [...this.querySelectorAll('[data-sl-moment]')];
    this.shown = 0;

    this.buttons.forEach((button) => button.addEventListener('click', () => this.toggle(button)));

    // The orchestrated moment: preselected walks fill in one after another
    // the first time the planner scrolls into view.
    const selected = this.buttons.filter((b) => b.getAttribute('aria-pressed') === 'true');
    if (reducedMotion() || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      this.shown = this.totalMinutes() * this.rate;
      this.render(false);
      return;
    }
    selected.forEach((b) => this.segment(b)?.classList.remove('is-on'));
    this.numberEl.textContent = '0';
    this.minutesEl.textContent = '0';
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        selected.forEach((b, i) => setTimeout(() => this.segment(b)?.classList.add('is-on'), 180 + i * 260));
        setTimeout(() => this.render(true, false), 180);
      },
      { threshold: 0.45 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  segment(button) {
    return this.querySelector(`[data-sl-seg="${button.dataset.slMoment}"]`);
  }

  totalMinutes() {
    return this.buttons
      .filter((b) => b.getAttribute('aria-pressed') === 'true')
      .reduce((sum, b) => sum + Number(b.dataset.minutes || 0), 0);
  }

  toggle(button) {
    const on = button.getAttribute('aria-pressed') !== 'true';
    button.setAttribute('aria-pressed', String(on));
    this.segment(button)?.classList.toggle('is-on', on);
    this.render(true, true);
  }

  render(animate, announce = false) {
    const minutes = this.totalMinutes();
    const target = minutes * this.rate;
    this.minutesEl.textContent = String(minutes);
    if (announce && this.liveEl) {
      const template = this.liveEl.dataset.template || '[steps] estimated steps from [minutes] minutes';
      this.liveEl.textContent = template.replace('[steps]', formatNumber(target)).replace('[minutes]', minutes);
    }
    cancelAnimationFrame(this.frame);
    if (!animate || reducedMotion()) {
      this.numberEl.textContent = formatNumber(target);
      this.shown = target;
      return;
    }
    const from = this.shown;
    const start = performance.now();
    const duration = Math.min(1400, 500 + Math.abs(target - from) / 12);
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const value = from + (target - from) * eased;
      this.numberEl.textContent = formatNumber(value);
      if (p < 1) {
        this.frame = requestAnimationFrame(tick);
      } else {
        this.shown = target;
      }
    };
    this.frame = requestAnimationFrame(tick);
  }
}
define('sl-day-planner', SaunterlyDayPlanner);

/* ---------- Tabs (WAI-ARIA tabs pattern, automatic activation) ---------- */
class SaunterlyTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.onKey(event, index));
    });
    this.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  onBlockSelect = (event) => {
    const index = this.tabs.findIndex((tab) => tab.contains(event.target) || tab.dataset.blockId === event.detail?.blockId);
    if (index > -1) this.select(index);
  };

  onKey(event, index) {
    const last = this.tabs.length - 1;
    const next = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: last }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const target = next > last ? 0 : next < 0 ? last : next;
    this.select(target);
    this.tabs[target].focus();
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
    this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
  }
}
define('sl-tabs', SaunterlyTabs);

/* ---------- Gallery: swipe track, thumbnails, zoom dialog ---------- */
class SaunterlyGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-sl-track]');
    this.slides = [...this.querySelectorAll('[data-sl-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-sl-thumb]')];
    this.dots = [...this.querySelectorAll('[data-sl-dot]')];
    this.dialog = this.querySelector('[data-sl-zoom]');
    this.zoomImg = this.querySelector('[data-sl-zoom-img]');
    this.index = 0;

    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i)));
    this.track?.addEventListener('scroll', this.onScroll, { passive: true });
    this.querySelectorAll('[data-sl-zoom-open]').forEach((button) =>
      button.addEventListener('click', () => this.openZoom(Number(button.dataset.index)))
    );
    this.querySelector('[data-sl-zoom-close]')?.addEventListener('click', () => this.dialog.close());
    this.querySelector('[data-sl-zoom-prev]')?.addEventListener('click', () => this.stepZoom(-1));
    this.querySelector('[data-sl-zoom-next]')?.addEventListener('click', () => this.stepZoom(1));
    this.zoomImg?.addEventListener('click', this.toggleZoom);
    this.zoomImg?.addEventListener('pointermove', this.panZoom);
    this.dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.stepZoom(1);
      if (event.key === 'ArrowLeft') this.stepZoom(-1);
    });
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  onScroll = () => {
    cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      const width = this.track.clientWidth || 1;
      const index = Math.round(this.track.scrollLeft / width);
      if (index !== this.index) this.mark(index);
    });
  };

  go(index) {
    const slide = this.slides[index];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reducedMotion() ? 'auto' : 'smooth' });
    this.mark(index);
  }

  mark(index) {
    this.index = index;
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }

  zoomable() {
    return this.slides.filter((slide) => slide.dataset.zoomSrc);
  }

  openZoom(index) {
    if (!this.dialog) return;
    this.zoomIndex = index;
    this.showZoom();
    this.dialog.showModal();
  }

  stepZoom(direction) {
    const items = this.zoomable();
    if (!items.length) return;
    const position = items.indexOf(this.slides[this.zoomIndex]);
    const next = items[(position + direction + items.length) % items.length];
    this.zoomIndex = this.slides.indexOf(next);
    this.showZoom();
  }

  showZoom() {
    const slide = this.slides[this.zoomIndex];
    if (!slide) return;
    this.zoomImg.classList.remove('is-zoomed');
    this.zoomImg.src = slide.dataset.zoomSrc;
    this.zoomImg.alt = slide.dataset.zoomAlt || '';
  }

  toggleZoom = (event) => {
    this.zoomImg.classList.toggle('is-zoomed');
    this.panZoom(event);
  };

  panZoom = (event) => {
    if (!this.zoomImg.classList.contains('is-zoomed')) return;
    const rect = this.zoomImg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    this.zoomImg.style.transformOrigin = `${x}% ${y}%`;
  };
}
define('sl-gallery', SaunterlyGallery);

/* ---------- Buy box: setup choice, variant select, add to cart, sticky bar ---------- */
class SaunterlyBuyBox extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[data-sl-buy-form]');
    if (!this.form) return;
    this.priceEl = this.querySelector('[data-sl-price]');
    this.perDayEl = this.querySelector('[data-sl-perday]');
    this.messageEl = this.querySelector('[data-sl-buy-msg]');
    this.addButton = this.querySelector('[data-sl-add]');
    this.variantSelect = this.querySelector('[data-sl-variant-select]');
    this.mainRadio = this.querySelector('[data-sl-main-option]');
    this.bar = document.querySelector(`[data-sl-bar="${this.dataset.sectionId}"]`);
    this.barPrice = this.bar?.querySelector('[data-sl-bar-price]');
    this.barName = this.bar?.querySelector('[data-sl-bar-name]');
    this.barButton = this.bar?.querySelector('[data-sl-bar-add]');

    this.form.addEventListener('change', this.onChange);
    this.form.addEventListener('submit', this.onSubmit);
    this.variantSelect?.addEventListener('change', this.onVariant);
    this.update();
    this.watchBar();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onWindowScroll) window.removeEventListener('scroll', this.onWindowScroll);
  }

  selected() {
    return this.form.querySelector('input[name="id"]:checked');
  }

  onVariant = () => {
    const option = this.variantSelect.selectedOptions[0];
    if (!option || !this.mainRadio) return;
    this.mainRadio.value = option.value;
    this.mainRadio.dataset.price = option.dataset.price;
    this.mainRadio.dataset.perDay = option.dataset.perDay;
    this.mainRadio.dataset.available = option.dataset.available;
    this.mainRadio.dataset.name = option.dataset.name;
    const priceLabel = this.mainRadio.closest('.sl-opt')?.querySelector('[data-sl-opt-price]');
    if (priceLabel) priceLabel.textContent = option.dataset.price;
    this.mainRadio.checked = true;
    this.update();
  };

  onChange = (event) => {
    if (event.target.name === 'id') this.update();
  };

  update(clearMessage = true) {
    const input = this.selected();
    if (!input) return;
    const available = input.dataset.available !== 'false';
    if (this.priceEl) this.priceEl.textContent = input.dataset.price;
    if (this.perDayEl && input.dataset.perDay) {
      this.perDayEl.textContent = this.perDayEl.dataset.template.replace('[per_day]', input.dataset.perDay);
    }
    if (this.barPrice) this.barPrice.textContent = input.dataset.price;
    if (this.barName) this.barName.textContent = input.dataset.name;
    [this.addButton, this.barButton].forEach((button) => {
      if (!button) return;
      button.disabled = !available;
      const label = button.querySelector('[data-sl-add-label]') || button;
      label.textContent = available ? button.dataset.labelAdd : button.dataset.labelSoldOut;
    });
    if (clearMessage && this.messageEl) {
      this.messageEl.textContent = '';
      this.messageEl.classList.remove('is-error');
    }
  }

  onSubmit = async (event) => {
    const input = this.selected();
    if (!input || !window.fetch) return;
    event.preventDefault();
    const buttons = [this.addButton, this.barButton].filter(Boolean);
    buttons.forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    });
    try {
      await addToCart(this, input.value, 1);
      const added = (this.dataset.addedText || '[name] added to your cart.').replace('[name]', input.dataset.name);
      this.messageEl.textContent = added;
      if (!openCartDrawer()) toast(added);
    } catch (error) {
      const message = error && error.message && error.message !== 'add-failed' ? error.message : this.dataset.errorText;
      this.messageEl.textContent = message || "Couldn't add to cart. Refresh the page and try again.";
      this.messageEl.classList.add('is-error');
    } finally {
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
      this.update(false);
    }
  };

  // The bar slides in once the visitor has scrolled past the first screen and hides
  // again whenever the buy box's own button is on screen.
  watchBar() {
    if (!this.bar || !this.addButton || !('IntersectionObserver' in window)) return;
    let buttonVisible = false;
    const sync = () => {
      // On the product page the bar shows whenever the main button is off screen (also before it's
      // reached, so phones have a buy button on the first screen); elsewhere only after scrolling.
      const scrolled = this.dataset.barEarly === 'true' || window.scrollY > window.innerHeight * 0.6;
      const show = !buttonVisible && scrolled;
      if (show === this.bar.classList.contains('is-visible')) return;
      this.bar.classList.toggle('is-visible', show);
      this.bar.toggleAttribute('inert', !show);
    };
    this.observer = new IntersectionObserver(([entry]) => {
      buttonVisible = entry.isIntersecting;
      sync();
    });
    this.observer.observe(this.addButton);
    this.onWindowScroll = () => sync();
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    sync();
  }
}
define('sl-buy-box', SaunterlyBuyBox);

/* ---------- 30-day streak preview ---------- */
class SaunterlyStreak extends HTMLElement {
  connectedCallback() {
    this.countEl = this.querySelector('[data-sl-streak-count]');
    this.days = [...this.querySelectorAll('[data-sl-day]')];
    this.days.forEach((day) =>
      day.addEventListener('click', () => {
        day.setAttribute('aria-pressed', String(day.getAttribute('aria-pressed') !== 'true'));
        this.count();
      })
    );
    this.count();
    this.autofill();
  }

  // The grid fills itself day by day the first time it scrolls into view, then stays interactive.
  autofill() {
    if (window.Shopify?.designMode) return;
    const fillAll = () => {
      this.days.forEach((d) => d.setAttribute('aria-pressed', 'true'));
      this.count();
    };
    if (reducedMotion() || !('IntersectionObserver' in window)) return fillAll();
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        this.days.forEach((d, i) =>
          setTimeout(() => {
            d.setAttribute('aria-pressed', 'true');
            if (i === this.days.length - 1) this.count();
          }, 60 * i)
        );
      },
      { threshold: 0.5 }
    );
    observer.observe(this);
  }

  count() {
    const done = this.days.filter((d) => d.getAttribute('aria-pressed') === 'true').length;
    const template = this.countEl?.dataset.template || '[done] of [total]';
    if (this.countEl) this.countEl.textContent = template.replace('[done]', done).replace('[total]', this.days.length);
  }
}
define('sl-streak', SaunterlyStreak);

/* ---------- Video reel: play muted loops only while visible ---------- */
class SaunterlyReel extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-sl-video]')];
    if (!this.items.length) return;
    const autoplay = !reducedMotion() && !window.Shopify?.designMode;

    this.items.forEach((item) => {
      const video = item.querySelector('video');
      const toggle = item.querySelector('[data-sl-video-toggle]');
      if (!video) return;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      if (autoplay) video.removeAttribute('controls');
      item.dataset.userPaused = autoplay ? 'false' : 'true';
      if (toggle) {
        toggle.hidden = !autoplay;
        toggle.addEventListener('click', () => {
          const paused = video.paused;
          item.dataset.userPaused = paused ? 'false' : 'true';
          paused ? this.play(video) : video.pause();
          this.syncToggle(item);
        });
      }
      video.addEventListener('play', () => this.syncToggle(item));
      video.addEventListener('pause', () => this.syncToggle(item));
    });

    if (!autoplay || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting && entry.target.dataset.userPaused !== 'true') {
            this.play(video);
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );
    this.items.forEach((item) => this.observer.observe(item));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  play(video) {
    if (video.preload === 'none') video.preload = 'auto';
    const attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(() => {});
  }

  syncToggle(item) {
    const video = item.querySelector('video');
    const toggle = item.querySelector('[data-sl-video-toggle]');
    if (!video || !toggle) return;
    const playing = !video.paused;
    toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
    toggle.querySelector('[data-icon-pause]').hidden = !playing;
    toggle.querySelector('[data-icon-play]').hidden = playing;
  }
}
define('sl-reel', SaunterlyReel);

/* ---------- 30-day reset tracker (start date and ticks saved in this browser only) ---------- */
const DAY_MS = 24 * 60 * 60 * 1000;
const dayStamp = (date) => Math.floor((date.getTime() - date.getTimezoneOffset() * 60000) / DAY_MS);

class SaunterlyReset extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.key;
    this.items = [...this.querySelectorAll('.sl-reset__day')];
    this.boxes = [...this.querySelectorAll('[data-sl-reset-day]')];
    this.countEl = this.querySelector('[data-sl-reset-count]');
    this.streakEl = this.querySelector('[data-sl-reset-streak]');
    this.barEl = this.querySelector('[data-sl-reset-bar]');
    this.startEl = this.querySelector('[data-sl-reset-start]');
    this.todayEl = this.querySelector('[data-sl-reset-today]');
    this.doneButton = this.querySelector('[data-sl-reset-done]');
    const saved = this.read('', []);
    this.boxes.forEach((box) => {
      box.checked = saved.includes(Number(box.value));
      box.addEventListener('change', () => this.save());
    });
    this.querySelector('[data-sl-reset-clear]')?.addEventListener('click', () => {
      this.boxes.forEach((box) => (box.checked = false));
      this.write('-start', null);
      this.save();
    });
    this.querySelector('[data-sl-reset-begin]')?.addEventListener('click', () => {
      this.write('-start', dayStamp(new Date()));
      this.render();
      this.todayEl?.querySelector('[data-sl-reset-done]')?.focus();
    });
    this.doneButton?.addEventListener('click', () => {
      const box = this.boxes[this.today() - 1];
      if (!box) return;
      box.checked = !box.checked;
      this.save();
    });
    this.querySelectorAll('[data-sl-reset-print]').forEach((b) => b.addEventListener('click', () => this.print()));
    this.render();
  }

  read(suffix, fallback) {
    try {
      const value = localStorage.getItem(this.key + suffix);
      return value === null ? fallback : JSON.parse(value);
    } catch (e) {
      return fallback;
    }
  }

  write(suffix, value) {
    try {
      if (value === null) localStorage.removeItem(this.key + suffix);
      else localStorage.setItem(this.key + suffix, JSON.stringify(value));
    } catch (e) {
      /* storage blocked: progress just isn't remembered */
    }
  }

  // Day number since the visitor pressed "Start Day 1" (1-based), or 0 when not started.
  today() {
    const start = this.read('-start', null);
    if (start === null) return 0;
    return Math.max(1, dayStamp(new Date()) - start + 1);
  }

  save() {
    this.write('', this.boxes.filter((b) => b.checked).map((b) => Number(b.value)));
    this.render();
  }

  print() {
    document.documentElement.classList.add('sl-printing-reset');
    const done = () => {
      document.documentElement.classList.remove('sl-printing-reset');
      window.removeEventListener('afterprint', done);
    };
    window.addEventListener('afterprint', done);
    window.print();
  }

  render() {
    const total = this.boxes.length;
    const done = this.boxes.filter((b) => b.checked).length;
    // Streak: consecutive ticked days ending at the latest ticked day.
    let last = 0;
    this.boxes.forEach((b, i) => {
      if (b.checked) last = i + 1;
    });
    let streak = 0;
    for (let i = last - 1; i >= 0 && this.boxes[i].checked; i--) streak++;
    if (this.countEl) this.countEl.textContent = this.countEl.dataset.template.replace('[done]', done).replace('[total]', total);
    if (this.streakEl) this.streakEl.textContent = this.streakEl.dataset.template.replace('[streak]', streak);
    if (this.barEl) this.barEl.style.width = `${total ? (done / total) * 100 : 0}%`;

    const day = this.today();
    if (this.startEl) this.startEl.hidden = day > 0;
    if (this.todayEl) this.todayEl.hidden = day === 0;
    this.items.forEach((li, i) => li.classList.toggle('is-today', i + 1 === day));
    if (!day || !this.todayEl) return;
    const label = this.todayEl.querySelector('[data-sl-reset-today-label]');
    const task = this.todayEl.querySelector('[data-sl-reset-today-task]');
    const extra = this.todayEl.querySelector('[data-sl-reset-today-extra]');
    if (day > total) {
      label.textContent = '';
      task.textContent = this.dataset.finishedText || '';
      extra.textContent = '';
      if (this.doneButton) this.doneButton.hidden = true;
      return;
    }
    const item = this.items[day - 1];
    label.textContent = (this.dataset.todayTemplate || 'Day [day] of [total]').replace('[day]', day).replace('[total]', total);
    task.textContent = item?.dataset.task || '';
    extra.textContent = item?.dataset.extra || '';
    if (this.doneButton) {
      this.doneButton.hidden = false;
      const checked = this.boxes[day - 1]?.checked;
      this.doneButton.textContent = checked ? this.doneButton.dataset.labelUndo : this.doneButton.dataset.labelDone;
      this.doneButton.setAttribute('aria-pressed', String(Boolean(checked)));
    }
  }
}
define('sl-reset', SaunterlyReset);

/* ---------- 3-question walking plan ---------- */
class SaunterlyQuiz extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-sl-quiz-form]');
    this.result = this.querySelector('[data-sl-quiz-result]');
    this.hint = this.querySelector('[data-sl-quiz-hint]');
    this.form?.addEventListener('change', () => this.render());
    this.form?.addEventListener('submit', (e) => e.preventDefault());
    this.render();
  }

  render() {
    const when = this.form.querySelector('input[name="when"]:checked');
    const minutesInput = this.form.querySelector('input[name="minutes"]:checked');
    const focus = this.form.querySelector('input[name="focus"]:checked');
    if (!when || !minutesInput || !focus) return;
    const cap = Number(when.dataset.cap) || 60;
    const minutes = Math.min(Number(minutesInput.value), cap);
    const steps = minutes * (Number(this.dataset.rate) || 100);
    const planText = when.dataset.plan || '';
    const plan = (this.dataset.plan || '[plan], about [minutes] minutes a day.')
      .replace('[plan]', planText.charAt(0).toUpperCase() + planText.slice(1))
      .replace('[minutes]', minutes);
    this.querySelector('[data-sl-quiz-plan]').textContent = plan;
    this.querySelector('[data-sl-quiz-tip]').textContent = focus.dataset.tip || '';
    this.querySelector('[data-sl-quiz-steps]').textContent = (this.dataset.steps || '').replace('[steps]', formatNumber(steps));
    const note = this.querySelector('[data-sl-quiz-note]');
    if (note) note.value = `Quiz plan: ${plan} ${focus.dataset.tip || ''}`.trim();
    const firstShow = this.result.hidden;
    this.result.hidden = false;
    if (this.hint) this.hint.hidden = true;
    if (firstShow && !reducedMotion()) this.result.classList.add('is-entering');
  }
}
define('sl-quiz', SaunterlyQuiz);

/* ---------- Will it fit? (dimensions come from the merchant's spec sheet) ---------- */
class SaunterlyFit extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-sl-fit-form]');
    this.out = this.querySelector('[data-sl-fit-result]');
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.check();
    });
  }

  check() {
    const data = new FormData(this.form);
    const inches = data.get('unit') === 'in';
    const toCm = (v) => (inches ? v * 2.54 : v);
    const height = toCm(parseFloat(data.get('height')));
    const depth = toCm(parseFloat(data.get('depth')));
    if (!(height > 0) || !(depth > 0)) {
      this.form.reportValidity();
      return;
    }
    const length = parseFloat(this.dataset.length);
    const folded = parseFloat(this.dataset.folded);
    const fmt = (cm) => (inches ? `${(cm / 2.54).toFixed(1)} in` : `${cm.toFixed(1)} cm`);
    const gap = height - folded;
    let text;
    let state;
    if (gap < 0) {
      text = this.dataset.noHeight.replace('[over]', fmt(-gap));
      state = 'no';
    } else if (gap < 2) {
      text = this.dataset.tightHeight.replace('[gap]', fmt(gap));
      state = 'tight';
    } else if (depth < length) {
      text = this.dataset.tightDepth.replace('[over]', fmt(length - depth));
      state = 'tight';
    } else {
      text = this.dataset.yes.replace('[gap]', fmt(gap));
      state = 'yes';
    }
    this.out.textContent = text;
    this.out.dataset.state = state;
  }
}
define('sl-fit', SaunterlyFit);

/* ---------- Member reviews: "Show more" on desktop ---------- */
class SaunterlyReviewWall extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('[data-sl-more]');
    if (!button) return;
    button.addEventListener('click', () => {
      this.dataset.expanded = 'true';
      button.setAttribute('aria-expanded', 'true');
      button.hidden = true;
    });
  }
}
define('sl-review-wall', SaunterlyReviewWall);
